import { Component, DestroyRef, Inject, OnDestroy, OnInit, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { DeviceService } from '../../services/device-service/device.service';
import { finalize } from 'rxjs/operators';
import { CommonService } from '../../services/common.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UnsavedChangesTrackerService } from '../../services/unsaved-changes/unsaved-changes-tracker.service';
import {
  bindFormDirtyTracking,
  clearFormUnsavedChanges,
} from '../../services/unsaved-changes/unsaved-changes-form.util';
import {
  PERIPHERAL_ID_HINT,
  getPeripheralIdErrorMessage,
  getPeripheralIdValidators,
} from '../../services/peripheral-id.validators';

const EDIT_PERIPHERAL_DIRTY_SOURCE = 'edit-peripheral-id';

interface ApiError {
  message: string;
  statusCode: number;
  error: string;
}

interface ApiResponse {
  success: boolean;
  message: string;
}

@Component({
  selector: 'app-update-peripherals',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatDialogModule
  ],
  templateUrl: './update-peripherals.component.html',
  styleUrls: ['./update-peripherals.component.scss']
})
export class UpdatePeripheralsComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly unsavedChanges = inject(UnsavedChangesTrackerService);

  peripheralForm: FormGroup;
  isSubmitting = false;
  readonly peripheralIdHint = PERIPHERAL_ID_HINT;

  constructor(
    private dialogRef: MatDialogRef<UpdatePeripheralsComponent>,
    private fb: FormBuilder,
    private deviceService: DeviceService,
    public commonService: CommonService,
    private snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    console.log('Dialog Data:', this.data);
    this.peripheralForm = this.fb.group({
      peripheralId: [this.data?.peripheralId ?? '', getPeripheralIdValidators()]
    });
  }

  get peripheralIdErrorMessage(): string {
    const control = this.peripheralForm.get('peripheralId');
    if (!control || control.valid) {
      return '';
    }
    return getPeripheralIdErrorMessage(control.errors);
  }

  ngOnInit(): void {
    bindFormDirtyTracking(
      this.peripheralForm,
      EDIT_PERIPHERAL_DIRTY_SOURCE,
      this.unsavedChanges,
      this.destroyRef
    );
    clearFormUnsavedChanges(
      this.peripheralForm,
      EDIT_PERIPHERAL_DIRTY_SOURCE,
      this.unsavedChanges
    );
  }

  ngOnDestroy(): void {
    this.unsavedChanges.clear(EDIT_PERIPHERAL_DIRTY_SOURCE);
  }

  onUpdate(): void {
    if (this.peripheralForm.invalid) {
      this.peripheralForm.markAllAsTouched();
      return;
    }

    const newPeripheralId = String(this.peripheralForm.value.peripheralId ?? '').trim();
    console.log('Updated Peripheral ID:', newPeripheralId);

    this.isSubmitting = true;
    this.deviceService.updatePeripheralId(this.data.id, newPeripheralId)
      .pipe(
        finalize(() => this.isSubmitting = false)
      )
      .subscribe({
        next: (response: any) => {
          console.log('Response received:', response);
          if (!response.success) {
            console.log('Update failed, message:', response.message);
            this.snackBar.open(response.message || 'Update failed', 'Close', {
              duration: 4000,
              horizontalPosition: 'end',
              verticalPosition: 'top'
            });
            return;
          }

          console.log('Peripheral ID updated successfully:', response);
          this.snackBar.open('Peripheral ID updated successfully', 'Close', {
            duration: 4000,
            horizontalPosition: 'end',
            verticalPosition: 'top'
          });
          clearFormUnsavedChanges(
            this.peripheralForm,
            EDIT_PERIPHERAL_DIRTY_SOURCE,
            this.unsavedChanges
          );
          this.commonService.reloadComponent();
          this.dialogRef.close({ success: true, peripheralId: newPeripheralId });
        },
        error: (error: ApiError) => {
          console.error('Error handler called with:', error);
          const response = error.error || error;
          const errorMessage = typeof response === 'string' ? response : response.message;
          this.snackBar.open(errorMessage || 'Update failed', 'Close', {
            duration: 4000,
            horizontalPosition: 'end',
            verticalPosition: 'top'
          });
        }
      });
  }
}
